import React, { useState } from "react";
import clsx from "clsx";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { List } from "react-window";
import { useInfiniteLoader } from "react-window-infinite-loader";

import { useAssets } from "../../../contexts/assets";
import ImageModal from "../../images/ImageModal";
import SortAssets from "../SortAssets";
import AssetItem from "./AssetItem";
import useInfinityScroll from "../../../hooks/capture/useInfinityScroll";
import AssetItemSkeleton from "./AssetItemSkeleton";
import { useCapture } from "../../../contexts/capture";

export const listRef = React.createRef();

const AssetRow = ({ style, index, collection, AssetItemComponent, currentAsset, handleChangeCurrentAsset, removeImage, selectedImage, onChangeImage, setModalAssetId, removeAsset, onSelectImage, selectedIds, unSelectBulk, bulkProps }) => (
  <div style={style} data-id="assets-list">
    {collection[index] ? (
      <AssetItemComponent
        asset={collection[index]}
        currentAsset={currentAsset}
        setCurrentAsset={handleChangeCurrentAsset}
        removeImage={removeImage}
        selectedImage={selectedImage}
        onChangeImage={onChangeImage}
        onOpenImageModal={setModalAssetId}
        onRemove={removeAsset}
        onSelectImage={onSelectImage}
        selectedIds={selectedIds}
        unSelectBulk={unSelectBulk}
        bulkProps={bulkProps}
      />
    ) : (
      <AssetItemSkeleton checkbox />
    )}
  </div>
);

export default function AssetList({
  height,
  selectedIds,
  unSelectBulk,
  bulkProps,
  AssetItemComponent = AssetItem,
  onSelectAsset,
}) {
  const { order, setOrder } = useCapture();
  const {
    loading,
    loadingMore,
    loadingMutation,
    collection,
    onLoadMore,
    totalCount,
    limit,
    currentAsset,
    setCurrentAsset,
    selectedImage,
    onChangeImage,
    removeAsset,
    onSelectImage,
    removeImage,
    updateImage,
    bulkCreateImages,
  } = useAssets();

  const [modalAssetId, setModalAssetId] = useState(null);
  const openedAsset = modalAssetId && collection.find(asset => asset.id === modalAssetId);

  const handleChangeCurrentAsset = value => {
    setCurrentAsset(value);
    onSelectAsset && onSelectAsset(value);
  };

  const { rowCount, loadMoreRows, isRowLoaded } = useInfinityScroll({
    entities: collection,
    totalCount,
    rowsCount: limit,
    onLoadMore,
    loading: loading || loadingMore || loadingMutation,
  });
  const onRowsRendered = useInfiniteLoader({ isRowLoaded, loadMoreRows, rowCount });

  const handleBulkCreateImages = ({ variables, onSuccess, onFailure }) => {
    bulkCreateImages({
      variables: { ...variables, assetId: openedAsset?.id },
      onSuccess,
      onFailure,
    });
  };

  return (
    <>
      {!loading && collection.length === 0 && (
        <Box textAlign="center" width="100%">
          <Typography variant="h2" color="textSecondary">
            Empty
          </Typography>
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Box>
          <SortAssets bulkProps={bulkProps} order={order} setOrder={setOrder} />
        </Box>
        {totalCount > 0 && <Typography sx={{ fontSize: '15px' }}>{totalCount} Items</Typography>}{" "}
      </Box>
      <Box display="flex" flexDirection="column" style={{ height, overflowY: "auto" }}>
        <List
          className={clsx({ "no-scrollbars": loadingMutation })}
          style={{ height, width: "100%" }}
          onRowsRendered={onRowsRendered}
          rowCount={rowCount}
          rowHeight={70}
          rowProps={{ collection, AssetItemComponent, currentAsset, handleChangeCurrentAsset, removeImage, selectedImage, onChangeImage, setModalAssetId, removeAsset, onSelectImage, selectedIds, unSelectBulk, bulkProps }}
          rowComponent={AssetRow}
        />
      </Box>
      {openedAsset && (
        <ImageModal
          open={Boolean(openedAsset?.id)}
          selectedId={selectedImage}
          asset={openedAsset}
          images={openedAsset.images}
          onClose={() => setModalAssetId(null)}
          onRemove={removeImage}
          onUpdate={updateImage}
          onCreate={handleBulkCreateImages}
        />
      )}
    </>
  );
}
